import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { db, auth } from "@/lib/firebase";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import MessageDialog from "@/components/ui/MessageDialog";

// Use the correct region for callable functions
const functions = getFunctions(undefined, "us-central1");
const createUserWithRole = httpsCallable(functions, "createUserWithRole");

const roleOptions = [
	{ value: "admin", label: "Admin" },
	{ value: "student", label: "Student" },
	{ value: "instructor", label: "Instructor" },
	{ value: "staff", label: "Staff" },
];

const Users: React.FC = () => {
		const [users, setUsers] = useState<any[]>([]);
		const [newUser, setNewUser] = useState({
			firstName: "",
			lastName: "",
			email: "",
			password: "",
			role: "student",
			phone: "",
			address: "",
			dateOfBirth: "",
			gender: "",
		});
		const [addingUser, setAddingUser] = useState(false);
		const [loading, setLoading] = useState(true);
		const [error, setError] = useState<string | null>(null);
		const [search, setSearch] = useState("");
		const [filterRole, setFilterRole] = useState("admin");
		const [branches, setBranches] = useState<any[]>([]);
		const [message, setMessage] = useState<{ open: boolean; text: string; type?: "success" | "error" | "info" }>({ open: false, text: "" });

		useEffect(() => {
			const fetchUsers = async () => {
				setLoading(true);
				setError(null);
				try {
					const usersSnap = await getDocs(collection(db, "users"));
					setUsers(usersSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
				} catch (err: any) {
					setError("Failed to load users.");
				} finally {
					setLoading(false);
				}
			};
			const fetchBranches = async () => {
				const branchesSnap = await getDocs(collection(db, "branches"));
				setBranches(branchesSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
			};
			fetchUsers();
			fetchBranches();
		}, []);

		const handleAddUser = async () => {
			if (!newUser.firstName.trim() || !newUser.lastName.trim() || !newUser.email.trim() || !newUser.password.trim()) {
				// Replace alert("All fields are required.");
				setMessage({ open: true, text: "All fields are required.", type: "error" });
				return;
			}
			if (!auth.currentUser) {
				console.error("No user is signed in!", auth.currentUser);
				// alert("You must be signed in as an admin to add users. Please log in again.");
				setMessage({ open: true, text: "You must be signed in as an admin to add users. Please log in again.", type: "error" });
				return;
			}
			console.log("Current user:", auth.currentUser);
			console.log("Firebase project ID:", auth.app.options.projectId);
			setAddingUser(true);
			try {
				// Force token refresh to ensure latest custom claims are used
				const idToken = await auth.currentUser.getIdToken(true);
				const decodedToken = await auth.currentUser.getIdTokenResult();
				console.log("Decoded token claims:", decodedToken.claims);
				if (!decodedToken.claims.admin) {
					// alert("You do not have admin privileges. Only admins can create users.");
					setMessage({ open: true, text: "You do not have admin privileges. Only admins can create users.", type: "error" });
					setAddingUser(false);
					return;
				}
				try {
					const result = await createUserWithRole({
						firstName: newUser.firstName,
						lastName: newUser.lastName,
						email: newUser.email,
						password: newUser.password,
						role: newUser.role,
						phone: newUser.phone,
						address: newUser.address,
						dateOfBirth: newUser.dateOfBirth,
						gender: newUser.gender,
					});
					console.log("createUserWithRole result:", result);
					// Refetch users after adding
					const usersSnap = await getDocs(collection(db, "users"));
					setUsers(usersSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
					setNewUser({ firstName: "", lastName: "", email: "", password: "", role: "student", phone: "", address: "", dateOfBirth: "", gender: "" });
					// alert("User created successfully!");
					setMessage({ open: true, text: "User created successfully!", type: "success" });
				} catch (error) {
					console.error("createUserWithRole error:", error);
					if (error.code) {
						console.error("Firebase error code:", error.code);
					}
					if (error.message) {
						console.error("Firebase error message:", error.message);
						// alert(`Failed to create user: ${error.message}`);
						setMessage({ open: true, text: `Failed to create user: ${error.message}`, type: "error" });
					} else {
						// alert("Failed to create user. See console for details.");
						setMessage({ open: true, text: "Failed to create user. See console for details.", type: "error" });
					}
				}
			} finally {
				setAddingUser(false);
			}
		};

		// Filter and search logic
		const filteredUsers = users.filter(
			(user) =>
				user.role === filterRole &&
				(`${user.firstName || ""} ${user.lastName || ""}`.toLowerCase().includes(search.toLowerCase()) ||
					user.email?.toLowerCase().includes(search.toLowerCase()))
		);

		return (
			<div>
				<h2 className="text-2xl font-bold mb-4">User Management</h2>
				<Card className="mb-6">
					<CardHeader>
						<CardTitle>Users</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="flex items-center gap-4 mb-4">
							<Input
								placeholder="Search users..."
								value={search}
								onChange={e => setSearch(e.target.value)}
								className="max-w-xs"
							/>
							<select
								className="border rounded px-3 py-2 bg-background text-foreground"
								value={filterRole}
								onChange={e => setFilterRole(e.target.value)}
							>
								<option value="admin">Admin</option>
								<option value="student">Student</option>
								<option value="instructor">Instructor</option>
								<option value="staff">Staff</option>
							</select>
						</div>
						{loading ? (
							<div className="py-8 text-center text-muted-foreground">Loading...</div>
						) : error ? (
							<div className="py-8 text-center text-destructive">{error}</div>
						) : filteredUsers.length === 0 ? (
							<div className="text-muted-foreground">No users found.</div>
						) : (
							<div className="space-y-2">
								{filteredUsers.map((user) => (
									<div key={user.id} className="flex items-center justify-between border rounded p-3">
										<div>
											<div className="font-medium">
												{(user.firstName || "") + " " + (user.lastName || "")} <span className="text-xs text-muted-foreground">({user.role})</span>
											</div>
											<div className="text-sm text-muted-foreground">{user.email}</div>
											<div className="text-xs text-muted-foreground">{user.phone} {user.gender && `• ${user.gender}`}</div>
											<div className="text-xs text-muted-foreground">{user.address} {user.dateOfBirth && `• ${user.dateOfBirth}`}</div>
											{user.role === "staff" && (
												<div className="mt-2">
													<label className="text-xs font-medium mr-2">Branch:</label>
													<select
														value={user.branch || ""}
														onChange={async (e) => {
															const branch = e.target.value;
															await updateDoc(doc(db, "users", user.id), { branch });
															setUsers(users.map(u => u.id === user.id ? { ...u, branch } : u));
															// alert(`Branch assigned: ${branch}`);
															setMessage({ open: true, text: `Branch assigned: ${branch}`, type: "success" });
														}}
														className="border rounded px-2 py-1"
													>
														<option value="">Unassigned</option>
														{branches.map((b: any) => (
															<option key={b.id} value={b.id}>{b.name || b.id}</option>
														))}
													</select>
												</div>
											)}
										</div>
										<div className="flex items-center gap-2 mt-2">
											<select
												value={user.role}
												onChange={async (e) => {
													const newRole = e.target.value;
													await updateDoc(doc(db, "users", user.id), { role: newRole });
													setUsers(users.map(u => u.id === user.id ? { ...u, role: newRole } : u));
													// alert(`Role updated to ${newRole}`);
													setMessage({ open: true, text: `Role updated to ${newRole}`, type: "success" });
												}}
												className="border rounded px-2 py-1"
											>
												<option value="admin">Admin</option>
												<option value="student">Student</option>
												<option value="instructor">Instructor</option>
												<option value="staff">Staff</option>
											</select>
										</div>
									</div>
								))}
							</div>
						)}
					</CardContent>
				</Card>
				<MessageDialog open={message.open} onClose={() => setMessage({ ...message, open: false })} message={message.text} type={message.type} />
			</div>
		);
	};

export default Users;
